// IE Hub - Enhanced Alert Component
// Wrapper around MUI Alert with IE Hub-specific styling and functionality

import React, { forwardRef, useCallback, useEffect, useState } from 'react';
import {
  AlertTitle,
  Box,
  IconButton,
  Alert as MuiAlert,
  Slide,
  Snackbar
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Warning as WarningIcon
} from '@mui/icons-material';

interface AlertProps {
  severity?: 'success' | 'info' | 'warning' | 'error';
  variant?: 'standard' | 'filled' | 'outlined';
  title?: string;
  children?: React.ReactNode;
  closable?: boolean;
  autoHide?: boolean;
  autoHideDuration?: number;
  onClose?: (event?: any, reason?: string) => void;
  action?: React.ReactNode;
  icon?: React.ComponentType<any> | null;
  [key: string]: any;
}

// Custom icons for different severities
const severityIcons = {
  success: CheckCircleIcon,
  info: InfoIcon,
  warning: WarningIcon,
  error: ErrorIcon
};

const Alert = forwardRef<HTMLDivElement, AlertProps>(({
  severity = 'info',
  variant = 'standard',
  title,
  children,
  closable = false,
  autoHide = false,
  autoHideDuration = 5000,
  onClose,
  action,
  icon,
  ...props
}, ref) => {
  const [open, setOpen] = useState(true);
  
  const handleClose = useCallback((event?: any, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }

    setOpen(false);

    if (onClose) {
      onClose(event, reason);
    }
  }, [onClose]);

  useEffect(() => {
    if (autoHide && autoHideDuration > 0) {
      const timer = setTimeout(() => {
        handleClose();
      }, autoHideDuration);
      
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [autoHide, autoHideDuration, handleClose]);
  
  const resolvedIcon = icon !== null ? (
    icon || severityIcons[severity as keyof typeof severityIcons]
  ) : null;

  // Support both component references (e.g. CheckCircleIcon) and JSX elements (e.g. <CheckCircleIcon />)
  const renderedIcon = resolvedIcon
    ? (React.isValidElement(resolvedIcon) ? resolvedIcon : React.createElement(resolvedIcon as React.ComponentType))
    : false;

  const alertAction = action || (closable && (
    <IconButton
      aria-label="close"
      color="inherit"
      size="small"
      onClick={handleClose}
    >
      <CloseIcon fontSize="inherit" />
    </IconButton>
  ));
  
  const alertContent = (
    <MuiAlert
      ref={ref}
      severity={severity}
      variant={variant}
      icon={renderedIcon}
      action={alertAction}
      sx={{
        borderRadius: 2,
        boxShadow: variant === 'filled' ? 2 : 0,
        '& .MuiAlert-message': {
          width: '100%'
        },
        ...(props.sx || {})
      }}
      {...props}
    >
      {title && (
        <AlertTitle sx={{ marginBottom: children ? 1 : 0 }}>
          {title}
        </AlertTitle>
      )}
      {children}
    </MuiAlert>
  );
  
  // Return plain alert if not using auto-hide
  if (!autoHide) {
    return open ? alertContent : null;
  }
  
  // Return Snackbar-wrapped alert for auto-hide functionality
  return (
    <Snackbar
      open={open}
      autoHideDuration={autoHideDuration}
      onClose={handleClose}
      TransitionComponent={Slide as any}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      sx={{ marginTop: 8 }}
    >
      <Box>
        {alertContent}
      </Box>
    </Snackbar>
  );
});

Alert.displayName = 'Alert';

// Toast options interface
interface ToastOptions {
  severity?: 'success' | 'info' | 'warning' | 'error';
  title?: string;
  duration?: number;
  variant?: 'standard' | 'filled' | 'outlined';
}

// Toast notification function
export const showToast = (message: string, options: ToastOptions = {}): () => void => {
  const {
    severity = 'info',
    title,
    duration = 5000,
    variant = 'filled'
  } = options;

  // This would typically integrate with a toast provider/context
  console.log('Toast:', { message, severity, title, duration, variant });

  // Return a cleanup function
  return () => {
    // Cleanup logic for removing the toast
  };
};

// Convenience functions for different alert types
export const showSuccess = (message: string, options: Omit<ToastOptions, 'severity'> = {}): () => void =>
  showToast(message, { ...options, severity: 'success' });

export const showError = (message: string, options: Omit<ToastOptions, 'severity'> = {}): () => void =>
  showToast(message, { ...options, severity: 'error' });

export const showWarning = (message: string, options: Omit<ToastOptions, 'severity'> = {}): () => void =>
  showToast(message, { ...options, severity: 'warning' });

export const showInfo = (message: string, options: Omit<ToastOptions, 'severity'> = {}): () => void =>
  showToast(message, { ...options, severity: 'info' });

export default Alert;
